import { useEffect, useState } from 'react';
import s from './ConfirmModal.module.scss';

/**
 * [2026-09-12 사용자] 브라우저 네이티브 alert/confirm/prompt 대체 — 인페이지 스타일 UI.
 *   WHY: window.alert/confirm/prompt는 OS 기본 대화상자라 브랜드/디자인과 이질적(사용자 지적, UX 조악).
 *   용도:
 *     - alert 대체  → alertMode=true (단일 [확인] 버튼, 취소 없음). onConfirm에서 후속 처리(예: 이동).
 *     - confirm 대체 → 기본(확인/취소). 위험 액션(탈퇴·삭제)은 danger=true(빨간 확인).
 *     - prompt 대체 → inputType 지정 시 입력창 노출(예: 탈퇴 비번). onConfirm(value)로 값 전달.
 *   접근성: role=dialog·aria-modal, Esc=취소, Enter=확인(입력 포함), 오버레이 클릭=취소.
 *   변경 금지 트리거: onConfirm은 비동기 액션을 호출할 수 있으니 호출측에서 닫기(setOpen(false)) 처리.
 */
export default function ConfirmModal({
  open, title, message, confirmText = '확인', cancelText = '취소',
  danger = false, alertMode = false, inputType, inputPlaceholder,
  onConfirm, onCancel,
}: {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  alertMode?: boolean;
  inputType?: 'password' | 'text';
  inputPlaceholder?: string;
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState('');
  useEffect(() => { if (open) setVal(''); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const submit = () => onConfirm(inputType ? val : undefined);

  return (
    <div className={s.overlay} data-lenis-prevent onClick={alertMode ? undefined : onCancel}>
      <div className={s.card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {title && <h3 className={s.title}>{title}</h3>}
        <p className={s.msg}>{message}</p>
        {inputType && (
          <input className={s.input} type={inputType} value={val} placeholder={inputPlaceholder}
            autoFocus onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        )}
        <div className={s.btns}>
          {!alertMode && <button type="button" className={s.cancel} onClick={onCancel}>{cancelText}</button>}
          <button type="button" className={`${s.confirm} ${danger ? s.danger : ''}`} onClick={submit}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}
